import React, { useEffect, useRef, useState } from 'react';
import {
    Check,
    Crown,
    Hand,
    Headphones,
    Loader2,
    Maximize2,
    Mic,
    MicOff,
    MonitorOff,
    MonitorUp,
    MoreVertical,
    Pencil,
    PhoneOff,
    Plus,
    Radio,
    Trash2,
    UserX,
    Users,
    Volume2,
    VolumeX,
    Wand2,
    X
} from 'lucide-react';
import { screenShareSupported, useVoice } from './VoiceProvider';

function initials(name) {
    return String(name || '?').split(/\s+/).filter(Boolean).slice(-2).map(part => part[0]).join('').toUpperCase();
}

function Avatar({ name, avatar, size = 'md' }) {
    return avatar
        ? <img className={`voice-avatar voice-avatar-${size}`} src={avatar} alt="" referrerPolicy="no-referrer" />
        : <span className={`voice-avatar voice-avatar-${size}`}>{initials(name)}</span>;
}

function TrackVideo({ track, muted = true }) {
    const ref = useRef(null);
    useEffect(() => {
        const element = ref.current;
        if (!element || !track) return undefined;
        track.attach(element);
        return () => { track.detach(element); };
    }, [track]);
    return <video ref={ref} autoPlay playsInline muted={muted} />;
}

function ScreenStage({ shares }) {
    const [pinnedUid, setPinnedUid] = useState(null);
    const frameRef = useRef(null);
    const pinned = shares.find(share => share.uid === pinnedUid) || shares[0];
    const others = shares.filter(share => share !== pinned);

    const openFullscreen = () => {
        const element = frameRef.current;
        if (!element) return;
        if (document.fullscreenElement) document.exitFullscreen();
        else element.requestFullscreen?.();
    };

    return (
        <div className="voice-stage">
            <div className="voice-stage-main" ref={frameRef} onDoubleClick={openFullscreen}>
                <TrackVideo track={pinned.screenTrack} />
                <div className="voice-stage-label">
                    <MonitorUp size={13} />
                    {pinned.isLocal ? 'Bạn đang chia sẻ màn hình' : `Màn hình của ${pinned.name}`}
                </div>
                <button type="button" className="voice-stage-fullscreen" onClick={openFullscreen} title="Toàn màn hình (hoặc nhấp đúp)">
                    <Maximize2 size={16} />
                </button>
            </div>
            {others.length > 0 && (
                <div className="voice-stage-strip">
                    {others.map(share => (
                        <button type="button" key={share.uid} className="voice-stage-thumb" onClick={() => setPinnedUid(share.uid)} title={`Xem màn hình của ${share.name}`}>
                            <TrackVideo track={share.screenTrack} />
                            <span>{share.isLocal ? 'Bạn' : share.name}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function ParticipantTile({ participant, room, isModerator, isAdmin, sharers, onAction }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const isOwner = room?.ownerUid === participant.uid;
    const isPrivileged = isOwner || participant.role === 'admin';
    const canShare = isPrivileged || sharers.includes(participant.uid);
    // Mirrors canActOnTarget on the server: admins can act on anyone, owners only on regular members.
    const canControl = isModerator && !participant.isLocal && (isAdmin || !isPrivileged);

    const act = (action, message) => {
        setMenuOpen(false);
        onAction(action, participant, message);
    };

    const items = [];
    if (isModerator && !participant.isLocal) {
        if (!canShare) items.push(<button key="grant" type="button" onClick={() => act('grant-share', `Đã cho ${participant.name} chia sẻ màn hình`)}><MonitorUp size={14} /> Cho phép chia sẻ màn hình</button>);
        if (canShare && !isPrivileged) items.push(<button key="revoke" type="button" onClick={() => act('revoke-share', `Đã thu hồi quyền chia sẻ của ${participant.name}`)}><MonitorOff size={14} /> Thu hồi quyền chia sẻ</button>);
        if (canControl && !participant.micMuted) items.push(<button key="mute" type="button" onClick={() => act('mute', `Đã tắt micro của ${participant.name}`)}><MicOff size={14} /> Tắt micro</button>);
        if (!isOwner) items.push(<button key="transfer" type="button" onClick={() => act('transfer', `${participant.name} giờ là chủ phòng`)}><Crown size={14} /> Chuyển chủ phòng</button>);
        if (isOwner && isAdmin) items.push(<button key="claim" type="button" onClick={() => act('claim', 'Bạn giờ là chủ phòng')}><Crown size={14} /> Nhận quyền chủ phòng</button>);
        if (canControl) items.push(<button key="kick" type="button" className="is-danger" onClick={() => act('kick', `Đã mời ${participant.name} ra khỏi phòng`)}><UserX size={14} /> Mời ra khỏi phòng</button>);
    }
    const showMenu = items.length > 0;

    return (
        <div className={`voice-tile ${participant.isSpeaking && !participant.micMuted ? 'is-speaking' : ''}`}>
            <Avatar name={participant.name} avatar={participant.avatar} size="lg" />
            <div className="voice-tile-name">
                {isOwner && <Crown size={12} className="voice-crown" aria-label="Chủ phòng" />}
                <span>{participant.name}{participant.isLocal ? ' (bạn)' : ''}</span>
            </div>
            <div className="voice-tile-badges">
                {participant.micMuted ? <MicOff size={13} className="is-off" /> : <Mic size={13} />}
                {participant.screenTrack && <MonitorUp size={13} className="is-live" />}
                {canShare && !isOwner && participant.role !== 'admin' && <span className="voice-chip">Được share</span>}
            </div>
            {showMenu && (
                <div className="voice-tile-menu">
                    <button type="button" className="voice-icon-btn" onClick={() => setMenuOpen(open => !open)} aria-label={`Tùy chọn cho ${participant.name}`}>
                        <MoreVertical size={15} />
                    </button>
                    {menuOpen && (
                        <div className="voice-menu" onMouseLeave={() => setMenuOpen(false)}>
                            {items}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function RoomCard({ room, members, isActive, isModerator, connecting, onJoin, onRename, onDelete }) {
    return (
        <div className={`voice-room-card ${isActive ? 'is-active' : ''}`}>
            <button type="button" className="voice-room-main" onClick={onJoin} disabled={connecting}>
                <span className="voice-room-icon">{isActive ? <Radio size={15} /> : <Volume2 size={15} />}</span>
                <span className="voice-room-text">
                    <strong>{room.name}</strong>
                    <small><Crown size={10} /> {room.ownerName || 'Không rõ'}</small>
                </span>
                {members.length > 0 && <span className="voice-room-count"><Users size={11} /> {members.length}</span>}
            </button>
            {members.length > 0 && (
                <div className="voice-room-members">
                    {members.map(member => (
                        <span key={member.uid} className="voice-room-member">
                            <Avatar name={member.name} avatar={member.avatar} size="xs" />
                            <span>{member.name}</span>
                            {member.sharing && <MonitorUp size={11} className="is-live" />}
                            {member.micMuted && <MicOff size={11} className="is-off" />}
                        </span>
                    ))}
                </div>
            )}
            {isModerator && (
                <div className="voice-room-tools">
                    <button type="button" onClick={onRename} title="Đổi tên phòng"><Pencil size={12} /></button>
                    <button type="button" onClick={onDelete} title="Xóa phòng"><Trash2 size={12} /></button>
                </div>
            )}
        </div>
    );
}

export default function VoiceView({ userRole, currentUid }) {
    const voice = useVoice();
    const [newRoomName, setNewRoomName] = useState('');
    const [creating, setCreating] = useState(false);
    const { refreshPresence, presenceInterval } = voice;

    useEffect(() => {
        refreshPresence();
        const timer = window.setInterval(() => {
            if (document.visibilityState === 'visible') refreshPresence();
        }, presenceInterval);
        return () => window.clearInterval(timer);
    }, [refreshPresence, presenceInterval]);

    const isAdmin = userRole === 'admin';
    const room = voice.activeRoom;
    const isModerator = !!room && (isAdmin || room.ownerUid === currentUid);
    const sharers = room?.sharers || [];
    const requests = Object.entries(room?.shareRequests || {}).sort((a, b) => (a[1]?.at || 0) - (b[1]?.at || 0));
    const myRequestPending = !!room?.shareRequests?.[currentUid];
    const shares = voice.participants.filter(participant => participant.screenTrack);
    const connected = voice.connection === 'connected' || voice.connection === 'reconnecting';

    const membersFor = (roomId) => {
        if (roomId === voice.activeRoomId && connected) {
            return voice.participants.map(p => ({ uid: p.uid, name: p.name, avatar: p.avatar, micMuted: p.micMuted, sharing: !!p.screenTrack }));
        }
        return voice.presence[roomId] || [];
    };

    const createRoom = async (event) => {
        event.preventDefault();
        if (!newRoomName.trim()) return;
        setCreating(true);
        const data = await voice.runAction('create', { name: newRoomName }, 'Đã tạo phòng');
        setCreating(false);
        if (data?.roomId) {
            setNewRoomName('');
            voice.join(data.roomId);
        }
    };

    const renameRoom = async (target) => {
        const name = window.prompt('Tên phòng mới', target.name);
        if (name && name.trim() !== target.name) await voice.runAction('rename', { roomId: target.id, name }, 'Đã đổi tên phòng');
    };

    const deleteRoom = async (target) => {
        if (!window.confirm(`Xóa phòng "${target.name}"? Mọi người đang trong phòng sẽ bị ngắt kết nối.`)) return;
        await voice.runAction('delete', { roomId: target.id }, 'Đã xóa phòng');
        refreshPresence();
    };

    const onParticipantAction = async (action, participant, message) => {
        if (action === 'kick' && !window.confirm(`Mời ${participant.name} ra khỏi phòng?`)) return;
        if (action === 'transfer' && !window.confirm(`Chuyển quyền chủ phòng cho ${participant.name}?`)) return;
        if (action === 'claim') {
            if (!window.confirm(`Nhận quyền chủ phòng từ ${participant.name}?`)) return;
            await voice.runAction('transfer', { roomId: room.id, targetUid: currentUid }, message);
            return;
        }
        await voice.runAction(action, { roomId: room.id, targetUid: participant.uid }, message);
    };

    const shareButton = () => {
        if (!screenShareSupported) {
            return <button type="button" className="voice-control" disabled title="Thiết bị này không hỗ trợ chia sẻ màn hình"><MonitorUp size={18} /><span>Không hỗ trợ</span></button>;
        }
        if (voice.isSharing) {
            return <button type="button" className="voice-control is-live" onClick={voice.toggleScreenShare}><MonitorOff size={18} /><span>Dừng chia sẻ</span></button>;
        }
        if (voice.canShare) {
            return <button type="button" className="voice-control" onClick={voice.toggleScreenShare}><MonitorUp size={18} /><span>Chia sẻ màn hình</span></button>;
        }
        if (myRequestPending) {
            return <button type="button" className="voice-control is-pending" onClick={() => voice.runAction('cancel-request', { roomId: room.id })}><Loader2 size={18} className="voice-spin" /><span>Đang chờ duyệt…</span></button>;
        }
        return <button type="button" className="voice-control" onClick={() => voice.runAction('request-share', { roomId: room.id }, 'Đã gửi yêu cầu tới chủ phòng')}><Hand size={18} /><span>Xin chia sẻ màn hình</span></button>;
    };

    return (
        <div className="voice-page">
            <aside className="voice-sidebar">
                <div className="voice-sidebar-head">
                    <div>
                        <h2>Phòng họp</h2>
                        <p>Nói chuyện và chia sẻ màn hình trong team</p>
                    </div>
                </div>
                <form className="voice-create" onSubmit={createRoom}>
                    <input value={newRoomName} onChange={event => setNewRoomName(event.target.value)} placeholder="Tên phòng mới…" maxLength={40} aria-label="Tên phòng mới" />
                    <button type="submit" disabled={creating || newRoomName.trim().length < 2} title="Tạo phòng"><Plus size={16} /></button>
                </form>
                <div className="voice-room-list">
                    {voice.rooms.length === 0 && <div className="voice-empty-small">Chưa có phòng nào. Tạo phòng đầu tiên ở trên.</div>}
                    {voice.rooms.map(item => (
                        <RoomCard
                            key={item.id}
                            room={item}
                            members={membersFor(item.id)}
                            isActive={item.id === voice.activeRoomId}
                            isModerator={isAdmin || item.ownerUid === currentUid}
                            connecting={voice.connection === 'connecting'}
                            onJoin={() => voice.join(item.id)}
                            onRename={() => renameRoom(item)}
                            onDelete={() => deleteRoom(item)}
                        />
                    ))}
                </div>
            </aside>

            <section className="voice-main">
                {!voice.activeRoomId && (
                    <div className="voice-welcome">
                        <span className="voice-welcome-icon"><Headphones size={28} /></span>
                        <h3>Chọn một phòng để bắt đầu</h3>
                        <p>Mọi thành viên đều tạo được phòng. Người tạo là chủ phòng và có quyền duyệt ai được chia sẻ màn hình.</p>
                        <ul>
                            <li><Mic size={14} /> Vào phòng là bật micro. Có thể tắt micro hoặc tắt loa bất cứ lúc nào.</li>
                            <li><Wand2 size={14} /> “Lọc ồn” dùng AI chạy ngay trên máy để loại tiếng quạt, gõ phím, xe cộ. Mặc định bật.</li>
                            <li><Hand size={14} /> Bấm “Xin chia sẻ màn hình” để gửi yêu cầu. Chủ phòng duyệt xong là bạn chia sẻ được.</li>
                            <li><Maximize2 size={14} /> Nhấp đúp vào màn hình đang chia sẻ để xem toàn màn hình.</li>
                            <li><Radio size={14} /> Chuyển sang tab khác vẫn giữ cuộc gọi, điều khiển ở thanh nhỏ góc dưới.</li>
                        </ul>
                    </div>
                )}

                {voice.activeRoomId && !connected && (
                    <div className="voice-welcome"><Loader2 size={28} className="voice-spin" /><h3>Đang kết nối…</h3></div>
                )}

                {room && connected && (
                    <>
                        <header className="voice-room-head">
                            <div>
                                <h3><Radio size={16} className="is-live" /> {room.name}</h3>
                                <p><Crown size={11} /> Chủ phòng: {room.ownerName || 'Không rõ'}{voice.connection === 'reconnecting' ? ' · Đang kết nối lại…' : ''}</p>
                            </div>
                            {!voice.canPlayAudio && <button type="button" className="voice-audio-unlock" onClick={voice.startAudio}><Volume2 size={14} /> Bật âm thanh</button>}
                        </header>

                        {isModerator && requests.length > 0 && (
                            <div className="voice-requests">
                                {requests.map(([uid, request]) => (
                                    <div key={uid} className="voice-request">
                                        <Hand size={15} />
                                        <span><strong>{request?.name || 'Thành viên'}</strong> xin chia sẻ màn hình</span>
                                        <button type="button" className="is-accept" onClick={() => voice.runAction('grant-share', { roomId: room.id, targetUid: uid }, `Đã cho ${request?.name || 'thành viên'} chia sẻ`)}><Check size={14} /> Cho phép</button>
                                        <button type="button" onClick={() => voice.runAction('deny-share', { roomId: room.id, targetUid: uid })}><X size={14} /> Từ chối</button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {shares.length > 0 && <ScreenStage shares={shares} />}

                        <div className={`voice-grid ${shares.length ? 'is-compact' : ''}`}>
                            {voice.participants.map(participant => (
                                <ParticipantTile
                                    key={participant.uid}
                                    participant={participant}
                                    room={room}
                                    isModerator={isModerator}
                                    isAdmin={isAdmin}
                                    sharers={sharers}
                                    onAction={onParticipantAction}
                                />
                            ))}
                        </div>

                        <div className="voice-controls">
                            <button type="button" className={`voice-control ${voice.micEnabled ? '' : 'is-off'}`} onClick={voice.toggleMic}>
                                {voice.micEnabled ? <Mic size={18} /> : <MicOff size={18} />}<span>{voice.micEnabled ? 'Tắt micro' : 'Bật micro'}</span>
                            </button>
                            <button type="button" className={`voice-control ${voice.deafened ? 'is-off' : ''}`} onClick={voice.toggleDeafen}>
                                {voice.deafened ? <VolumeX size={18} /> : <Headphones size={18} />}<span>{voice.deafened ? 'Bật loa' : 'Tắt loa'}</span>
                            </button>
                            {voice.noiseFilterSupported && (
                                <button type="button" className={`voice-control ${voice.noiseFilter ? 'is-on' : ''}`} onClick={voice.toggleNoiseFilter} title="Lọc tiếng quạt, gõ phím, xe cộ… khỏi micro của bạn">
                                    <Wand2 size={18} /><span>{voice.noiseFilter ? 'Lọc ồn: Bật' : 'Lọc ồn: Tắt'}</span>
                                </button>
                            )}
                            {shareButton()}
                            <button type="button" className="voice-control is-leave" onClick={voice.leave}><PhoneOff size={18} /><span>Rời phòng</span></button>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
}

export function VoiceMiniBar({ hidden, onOpen }) {
    const voice = useVoice();
    if (!voice || hidden || !voice.activeRoomId || voice.connection === 'idle') return null;
    const pending = voice.activeRoom && Object.keys(voice.activeRoom.shareRequests || {}).length;
    return (
        <div className="voice-minibar" role="region" aria-label="Cuộc gọi đang diễn ra">
            <button type="button" className="voice-minibar-room" onClick={onOpen} title="Mở phòng họp">
                <Radio size={14} className="is-live" />
                <span>
                    <strong>{voice.activeRoom?.name || 'Phòng họp'}</strong>
                    <small>{voice.connection === 'connected' ? `${voice.participants.length} người${voice.isSharing ? ' · Đang chia sẻ' : ''}${pending ? ` · ${pending} yêu cầu` : ''}` : 'Đang kết nối…'}</small>
                </span>
            </button>
            <button type="button" className={`voice-icon-btn ${voice.micEnabled ? '' : 'is-off'}`} onClick={voice.toggleMic} title={voice.micEnabled ? 'Tắt micro' : 'Bật micro'}>
                {voice.micEnabled ? <Mic size={15} /> : <MicOff size={15} />}
            </button>
            <button type="button" className={`voice-icon-btn ${voice.deafened ? 'is-off' : ''}`} onClick={voice.toggleDeafen} title={voice.deafened ? 'Bật loa' : 'Tắt loa'}>
                {voice.deafened ? <VolumeX size={15} /> : <Headphones size={15} />}
            </button>
            {voice.isSharing && (
                <button type="button" className="voice-icon-btn is-live" onClick={voice.toggleScreenShare} title="Dừng chia sẻ màn hình"><MonitorOff size={15} /></button>
            )}
            <button type="button" className="voice-icon-btn is-leave" onClick={voice.leave} title="Rời phòng"><PhoneOff size={15} /></button>
        </div>
    );
}
